// src/pages/BookViewer.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Menu, 
  X, 
  ChevronLeft, 
  ChevronRight, 
  Home, 
  Share2, 
  Bookmark,
  BookOpen,
  Calculator,
  Search,
  Settings,
  Palette,
  PlayCircle,
  FileText,
  Clock, 
  GraduationCap,
  Play, 
  Pause, 
  RotateCcw, 
  Save, 
  Send
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { firestoreService } from '../services/firestoreService';
import { MathRenderer } from '../components/math/MathRenderer';
import { MediaRenderer } from '../components/media/MediaRenderer';
import { InteractiveElementRenderer } from '../components/books/renderers/InteractiveElementRenderer';
import { BookSettingsModal } from '../components/books/BookSettingsModal';
import { ShareModal } from '../components/books/modals/ShareModal';
import { EnhancedCatalogTemplate } from '../components/templates/CatalogTemplate';
import { AlbumTemplate } from '../components/templates/AlbumTemplate';
import { ProfileTemplate } from '../components/templates/ProfileTemplate';
import { EventTemplate } from '../components/templates/EventTemplate';
import { EcommerceTemplate } from '../components/templates/EcommerceTemplate';
import { BookingTemplate } from '../components/templates/BookingTemplate';
import { InstitutionTemplate } from '../components/templates/InstitutionTemplate';
import { OrganisationTemplate } from '../components/templates/OrganisationTemplate';
import { DiscoveryTemplate } from '../components/templates/DiscoveryTemplate';
import { useViewManager } from '../hooks/useViewManager';
import { ViewToggle, ViewAuthModal } from '../components/common/ViewToggle';
import { analyticsService } from '../services/analyticsService';
import { FlipbookTemplate } from '../components/templates/FlipbookTemplate';
import { CourseTemplate } from '../components/templates/CourseTemplate';
import LeitnerTemplate from '../components/templates/LeitnerTemplate';
import { WebSearchTemplate } from '../components/templates/WebSearchTemplate';
import RequestsTemplate from '../components/templates/RequestsTemplate';
import DiaryTemplate from '../components/templates/DiaryTemplate';

const loadGoogleFont = (fontFamily) => {
  if (!fontFamily || fontFamily === 'Helvetica, sans-serif' || fontFamily === 'Arial, sans-serif') return;
  
  const fontName = fontFamily.split(',')[0].trim().replace(/['"]/g, '');
  const linkId = `google-font-${fontName.replace(/\s+/g, '-')}`;
  
  if (document.getElementById(linkId)) return;
  
  const link = document.createElement('link');
  link.id = linkId;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(/\s+/g, '+')}:wght@300;400;500;600;700&display=swap`;
  document.head.appendChild(link);
};


export const BookViewer = ({ 
  bookData: initialBookData, 
  bookId: propBookId, 
  isCustomDomain = false,      
  renderMode = 'standard',     
  hideNavigation = false,      
  isPublicBook = false  }) => {
  const [timer, setTimer] = useState({
    isActive: false,
    timeRemaining: null,
    startTime: null,
    isExpired: false
  });
  const { bookId: routeBookId } = useParams();
  const navigate = useNavigate();
  const { user, userProfile, logout } = useAuth();

  const bookId = propBookId || routeBookId;
  
  const [book, setBook] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sectionsData, setSectionsData] = useState([]);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tocOpen, setTocOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false); 
  const [userResponses, setUserResponses] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  const [responseId, setResponseId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [responseLoading, setResponseLoading] = useState(true);
  const [responseError, setResponseError] = useState(null);

  const [canEdit, setCanEdit] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [existingResponse, setExistingResponse] = useState(null);

  const viewManager = useViewManager({
    bookId,
    bookData: book,
    defaultView: 'public',
    requireAuth: true
  });

  // Close TOC on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setTocOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  
  useEffect(() => {
    let interval = null;
    
    if (timer.isActive && timer.timeRemaining > 0) {
      interval = setInterval(() => {
        setTimer(prev => {
          const newTimeRemaining = Math.max(0, prev.timeRemaining - 1);
          
          
          if (newTimeRemaining === 0 && !prev.isExpired) {
            handleAutoSubmit();
            return { ...prev, timeRemaining: 0, isExpired: true, isActive: false };
          }
          
          return { ...prev, timeRemaining: newTimeRemaining };
        });
      }, 1000);
    } else {
      clearInterval(interval);
    }
    
    return () => clearInterval(interval);
  }, [timer.isActive, timer.timeRemaining]);

  useEffect(() => {
    if (isCustomDomain && book) {
      // Apply custom CSS if provided
      if (book.customCSS) {
        const styleId = 'custom-domain-styles';
        let styleElement = document.getElementById(styleId);
        
        if (!styleElement) {
          styleElement = document.createElement('style');
          styleElement.id = styleId;
          document.head.appendChild(styleElement);
        }
        
        styleElement.textContent = book.customCSS;
      }
    }
  }, [isCustomDomain, book]);

  useEffect(() => {
    if (book) {
      const fontFamily = book?.styling?.fontFamily || book?.fontFamily;
      
      if (fontFamily) {
        loadGoogleFont(fontFamily);
        
        const styleId = 'book-custom-font';
        let styleElement = document.getElementById(styleId);
        
        if (!styleElement) {
          styleElement = document.createElement('style');
          styleElement.id = styleId;
          document.head.appendChild(styleElement);
        }
        
        styleElement.textContent = `
          body, body * {
            font-family: ${fontFamily} !important;
          }
        `;
      }
    }

    return () => {
      const styleElement = document.getElementById('book-custom-font');
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, [book?.styling?.fontFamily, book?.fontFamily]);

  // Load book data
  useEffect(() => {
    const loadBookData = async () => {
      if (!bookId) return;
      
      // If we already have book data from BookAccessGuard, just load messages
      if (initialBookData) {
        setBook(initialBookData);
        try {
          const messagesData = await firestoreService.getBookMessages(bookId);
          setMessages(messagesData);
          await initializeResponse();
        } catch (error) {
          console.error('Error loading messages:', error);
        } finally {
          setLoading(false);
        }
        return;
      }

      // Fallback: Load book data if not provided (shouldn't happen with BookAccessGuard)
      try {
        setLoading(true);
        const bookData = await firestoreService.getBook(bookId);
        
        if (!bookData) {
          navigate('/');
          return;
        }

        setBook(bookData);
        const messagesData = await firestoreService.getBookMessages(bookId);
        setMessages(messagesData);
        await initializeResponse();

      } catch (error) {
        console.error('Error loading book:', error);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    loadBookData();
  }, [bookId, initialBookData, user, navigate]);

  // Group messages by section
  useEffect(() => {
    if (messages.length > 0) {
      groupMessagesBySection();
    }
  }, [messages]);

  useEffect(() => {
    if (isCustomDomain && book) {
      // Track visit
      analyticsService.trackCustomDomainVisit(bookId, book);
      
      // Apply custom CSS
      if (book.customCSS) {
        const styleId = 'custom-domain-styles';
        let styleElement = document.getElementById(styleId);
        
        if (!styleElement) {
          styleElement = document.createElement('style');
          styleElement.id = styleId;
          document.head.appendChild(styleElement);
        }
        
        styleElement.textContent = book.customCSS;
      }
    }
  }, [isCustomDomain, book, bookId]);

  // Initialize response tracking
  const initializeResponse = async () => {
    try {
      setResponseLoading(true);

      let currentSessionId = sessionId;
      if (!currentSessionId) {
        currentSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem(`book_session_${bookId}`, currentSessionId);
      }
      setSessionId(currentSessionId);

      // Check for existing incomplete response
      const existingResponse = await firestoreService.getBookResponse(
        bookId, 
        user?.uid,
        currentSessionId
      );

      if (existingResponse && !existingResponse.metadata?.isSubmitted) {
        if (existingResponse.metadata?.allowResumeIncomplete) {
          setExistingResponse(existingResponse);
          setShowResumeModal(true);
          return;
        }
      }

      await createNewResponse(currentSessionId);
      
    } catch (error) {
      console.error('Error initializing response:', error);
      setResponseError(error.message);
    } finally {
      setResponseLoading(false);
    }
  };

  const createNewResponse = async (sessionId, options = {}) => {
    
    if (isEducationBook(book)) {
    try {
      // Get book settings for timing
      const bookSettings = book?.settings || {};
      const timeLimit = bookSettings.timeLimit || options.timeLimit;

      const newResponse = await firestoreService.createBookResponse(
        bookId,
        
        sessionId,
        {
          timeLimit,
          allowResumeIncomplete: bookSettings.allowResumeIncomplete !== false
        }
      );

      setResponseId(newResponse.id);
      setCanEdit(newResponse.metadata?.canEdit !== false);

      // Initialize timer if time limit is set
      if (newResponse.metadata?.timeLimit) {
        setTimer({
          isActive: true,
          timeRemaining: newResponse.metadata.timeRemaining,
          startTime: newResponse.metadata.startedAt,
          isExpired: false
        });
      }

      console.log('Created new response:', newResponse);
      
    } catch (error) {
      console.error('Error creating new response:', error);
      throw error;
    }
   }
  };

  const resumeExistingResponse = useCallback(async (response) => {
    try {
      setUserResponses(response.responses || {});
      setResponseId(response.responseId);
      setCanEdit(response.metadata?.canEdit !== false);

      // Resume timer if applicable
      if (response.metadata?.timeRemaining > 0 && !response.metadata?.isSubmitted) {
        setTimer({
          isActive: true,
          timeRemaining: response.metadata.timeRemaining,
          startTime: response.metadata.startedAt,
          isExpired: false
        });
      }

      setShowResumeModal(false);
      console.log('Resumed existing response:', response);
      
    } catch (error) {
      console.error('Error resuming response:', error);
    }
  }, []);

  const groupMessagesBySection = () => {
    const grouped = [];
    const sectionMap = new Map();

    messages.forEach((message) => {
      const sectionName = message.section || 'Untitled Section';
      
      if (!sectionMap.has(sectionName)) {
        const section = {
          name: sectionName,
          messages: [],
          index: grouped.length
        };
        grouped.push(section);
        sectionMap.set(sectionName, section);
      }
      
      sectionMap.get(sectionName).messages.push(message);
    });

    setSectionsData(grouped);
  };

  const handleNavigation = (direction) => {
    const newIndex = direction === 'next' 
      ? Math.min(currentSectionIndex + 1, sectionsData.length - 1)
      : Math.max(currentSectionIndex - 1, 0);
    
    setCurrentSectionIndex(newIndex);
    
    // Track interaction
    firestoreService.trackInteraction(bookId, {
      type: 'navigation',
      direction,
      sectionIndex: newIndex,
      userId: user?.uid
    });
  };

  const jumpToSection = (index) => {
    setCurrentSectionIndex(index);
    setTocOpen(false);
    
    // Track interaction
    firestoreService.trackInteraction(bookId, {
      type: 'section_jump',
      sectionIndex: index,
      userId: user?.uid
    });
  };

  const toggleToc = () => {
    setTocOpen(!tocOpen);
  };

  const closeToc = () => {
    setTocOpen(false);
  };

  // ADDED: Share functionality
  const handleShare = () => {
    setShareOpen(true);
    
    // Track share button click
    firestoreService.trackInteraction(bookId, {
      type: 'share_click',
      userId: user?.uid
    });
  };

  const handleBookUpdate = async (updatedBook) => {
    try {
      await firestoreService.updateBook(bookId, updatedBook);
      setBook(updatedBook);
    } catch (error) {
      console.error('Error updating book:', error);
    }
  };

  const handleUserResponse = useCallback(async (messageId, value, variable, messageData) => {
    if (!canEdit) {
      alert('This response cannot be edited as it has been submitted or expired.');
      return;
    }

    const trackingMode = messageData?.trackingMode || 'both';
    const mid = sanitiseSegment(toFieldSegment(messageId));
    const varSeg = sanitiseSegment(toFieldSegment(variable));

    const responseUpdate = {};
    switch (trackingMode) {
      case 'both':
        if (varSeg) responseUpdate[`responses.${varSeg}`] = value;
        if (mid) responseUpdate[`responses.${mid}`] = value;
        break;
      case 'variable':
        if (varSeg) {
          responseUpdate[`responses.${varSeg}`] = value;
        } else if (mid) {
          responseUpdate[`responses.${mid}`] = value;
        }
        break;
      case 'messageId':
      default:
        if (mid) responseUpdate[`responses.${mid}`] = value;
        break;
    }

    // Update local state
    const newResponses = {
      ...userResponses,
      ...Object.fromEntries(
        Object.entries(responseUpdate).map(([k, val]) => [k.replace(/^responses\./, ''), val])
      )
    };
    setUserResponses(newResponses);
    setHasUnsavedChanges(true);

    // Auto-save
    if (responseId && isEducationBook(book)) {
        try {
          await firestoreService.updateBookResponse(responseId, {
            ...responseUpdate,
            updatedAt: new Date()
          });
          setHasUnsavedChanges(false);
        } catch (error) {
          console.error('Error auto-saving response:', error);
        }
      }
  }, [responseId, userResponses, canEdit]);

  const isEducationBook = (book) => {
    if (!book) return false;
    
    const category = (book.botCategory || '').toLowerCase();
    const templateType = (book.templateType || '').toLowerCase();
    const bookName = (book.botName || '').toLowerCase();
    
    return (
      category === 'education' ||
      templateType === 'education' ||
      category.includes('education') ||
      category.includes('learning') ||
      category.includes('course') ||
      category.includes('tutorial') ||
      bookName.includes('grade') ||
      bookName.includes('math') ||
      bookName.includes('science') ||
      bookName.includes('lesson')
    );
  };


  const toFieldSegment = (v) => {
    if (v == null) return '';
    if (typeof v === 'string' || typeof v === 'number') return String(v);
    if (typeof v === 'object') {
      // common cases
      if (v.id) return String(v.id);
      if (v.messageId) return String(v.messageId);
      // last resort
      return '';
    }
    return String(v);
  };

  const sanitiseSegment = (s) => String(s).replace(/[~*/\[\]]/g, '_'); // Firestore banned chars

  const handleManualSave = async () => {
    if (!responseId || !hasUnsavedChanges) return;

    try {
      await firestoreService.updateBookResponse(responseId, {
        responses: userResponses,
        updatedAt: new Date()
      });
      setHasUnsavedChanges(false);
      // Show success message
    } catch (error) {
      console.error('Error saving manually:', error);
    }
  };

  const handleSubmit = async () => {
    if (!responseId) return;

    const confirmSubmit = window.confirm(
      'Are you sure you want to submit your response? You will not be able to edit it after submission.'
    );

    if (!confirmSubmit) return;

    try {
      await firestoreService.submitBookResponse(responseId, {
        finalResponses: userResponses,
        submissionMethod: 'manual'
      });
      
      setCanEdit(false);
      setTimer(prev => ({ ...prev, isActive: false }));
      
      // Show success message
      alert('Response submitted successfully!');
      
    } catch (error) {
      console.error('Error submitting response:', error);
      alert('Failed to submit response. Please try again.');
    }
  };
  
  const handleAutoSubmit = async () => {
    if (!responseId) return;

    try {
      await firestoreService.submitBookResponse(responseId, {
        finalResponses: userResponses,
        submissionMethod: 'auto',
        reason: 'time_expired'
      });
      
      setCanEdit(false);
      alert('Time expired! Your response has been automatically submitted.');
      
    } catch (error) {
      console.error('Error auto-submitting response:', error);
    }
  };
  
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const updateProgress = async (section, isComplete = false) => {
    if (!responseId) return;

    try {
      await firestoreService.updateResponseProgress(responseId, section, isComplete);
      console.log('Progress updated:', { section, isComplete });
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  

  const ResumeModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold mb-4">Resume Previous Response?</h3>
        <p className="text-gray-600 mb-6">
          You have an incomplete response for this book. Would you like to continue where you left off or start fresh?
        </p>
        
        {existingResponse?.metadata?.timeRemaining > 0 && (
          <div className="bg-blue-50 p-3 rounded-lg mb-4">
            <div className="flex items-center gap-2 text-blue-700">
              <Clock className="h-4 w-4" />
              <span className="font-medium">
                Time remaining: {formatTime(existingResponse.metadata.timeRemaining)}
              </span>
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={() => resumeExistingResponse(existingResponse)}
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
          >
            <Play className="h-4 w-4" />
            Resume
          </button>
          <button
            onClick={() => {
              setShowResumeModal(false);
              createNewResponse(sessionId);
            }}
            className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 flex items-center justify-center gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Start Fresh
          </button>
        </div>
      </div>
    </div>
  );

  // Timer Display Component
  const TimerDisplay = () => {
    if (!timer.timeRemaining) return null;

    const isWarning = timer.timeRemaining <= 300; // 5 minutes
    const isCritical = timer.timeRemaining <= 60; // 1 minute

    return (
      <div className={`fixed top-4 right-4 p-3 rounded-lg shadow-lg z-40 ${
        isCritical ? 'bg-red-100 border border-red-300' :
        isWarning ? 'bg-yellow-100 border border-yellow-300' :
        'bg-blue-100 border border-blue-300'
      }`}>
        <div className="flex items-center gap-2">
          <Clock className={`h-4 w-4 ${
            isCritical ? 'text-red-600' :
            isWarning ? 'text-yellow-600' :
            'text-blue-600'
          }`} />
          <span className={`font-mono font-medium ${
            isCritical ? 'text-red-700' :
            isWarning ? 'text-yellow-700' :
            'text-blue-700'
          }`}>
            {formatTime(timer.timeRemaining)}
          </span>
        </div>
      </div>
    );
  };

  // Control Bar Component
  const ControlBar = () => (
    <div className="fixed top-16 right-4 flex gap-2 z-40">
      {hasUnsavedChanges && (
        <button
          onClick={handleManualSave}
          className="bg-white text-yellow-600 px-2 py-2 rounded-lg hover:bg-yellow-700 hover:text-white flex items-center gap-2 shadow-xl"
        >
          <Save className="h-4 w-4" />
          Save
        </button>
      )}
      
      {canEdit && (
        <button
          onClick={handleSubmit}
          className="bg-white text-green-700 px-2 py-2 rounded-lg hover:bg-green-700 hover:text-white flex items-center gap-2 shadow-xl"
        >
          <Send className="h-4 w-4" />
          Submit
        </button>
      )}
    </div>
  );

  const renderMessage = (message, index) => {
    const hasMedia = message.mediaUrl && message.mediaUrl.trim() !== '';
    const hasInteractiveElement = message.userResponseType && message.userResponseType.trim() !== '';

    return (
      <div key={`${message.messageId}-${index}`} className="message-item mb-4">
        {/* Message Content */}
        <div className="prose prose-lg max-w-none mb-2">
          <MathRenderer 
            content={message.message}
            enableMarkdown={true}
            enableMath={true}
            userResponses={userResponses} // Pass userResponses for variable interpolation
            className="text-gray-800 leading-relaxed"
          />
        </div>

        {/* Media Content */}
        {hasMedia && (
          <div className="media-content mb-2">
            <MediaRenderer 
              mediaUrl={message.mediaUrl}
              alt={`Media for ${message.section || 'content'}`}
              className="rounded-lg shadow-sm"
            />
          </div>
        )}

        {/* Interactive Elements */}
        {hasInteractiveElement && (
          <div className="interactive-element bg-blue-50 rounded-lg p-2 border border-blue-100 mb-2">
            <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Your Response
              {message.variable && (
                <span className="text-sm font-mono text-blue-600">
                  {message.variable}
                </span>
              )}
            </h3>
            
              <InteractiveElementRenderer
                message={message}
                value={userResponses[message.variable || message.messageId] || ''}
                onChange={(value, variable) => handleUserResponse(message.messageId, value, variable)}
                disabled={false}
                responseId={responseId}
                bookId={bookId}
                userResponses={userResponses}
              />
          </div>
        )}
      </div>
    );
  };

  const currentSection = sectionsData[currentSectionIndex];
  const progress = sectionsData.length > 0 ? 
    ((currentSectionIndex + 1) / sectionsData.length) * 100 : 0;

  // Dynamic styling from book metadata
  const bookStyles = {
    '--book-primary': book?.primaryColor || '#2563eb',
    '--book-background': book?.backgroundColor || '#f8fafc',
    '--book-header': book?.headerColor || '#2563eb',
    fontFamily: book?.fontFamily || 'Helvetica, Arial, sans-serif'
  };

  const stableCustomStyling = useMemo(() => ({
    accentColor: book?.styling?.accentColor || '#ed1566',
    primaryColor: book?.styling?.primaryColor || '#050807ff',
    backgroundColor: book?.styling?.backgroundColor || '#f8fafc',
    headerColor: book?.styling?.headerColor || '#ffffff',
    fontFamily: book?.styling?.fontFamily || 'Helvetica, sans-serif'
  }), [
    book?.styling?.accentColor,
    book?.styling?.primaryColor,
    book?.styling?.backgroundColor,
    book?.styling?.headerColor,
    book?.styling?.fontFamily
  ]);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading book...</p>
        </div>
      </div>
    );
  }

  

  if (book?.templateType === 'catalog') {
    return (
      <EnhancedCatalogTemplate
        user={userProfile}
        bookId={bookId}
        bookData={book}
        styling={{
          primaryColor: book?.styling?.primaryColor || '#0795ff',
          backgroundColor: book?.styling?.backgroundColor || book?.backgroundColor || '#f8fafc',
          headerColor: book?.styling?.headerColor || book?.headerColor || '#ffffff',
          headerOpacity: book?.styling?.headerOpacity ?? book?.headerOpacity ?? 100,
          textColor: book?.styling?.textColor || '#3b3c26',
          fontFamily: book?.fontFamily || 'Helvetica, sans-serif'
        }}
      />
    );
  }

  if (book?.templateType === 'album') {
    return (
      <>
      <AlbumTemplate
        key={`album-${viewManager.viewMode}`}
        user={userProfile}
        bookId={bookId}
        bookData={book}
        viewManager={viewManager}
        customStyling={{
          primaryColor: book?.styling?.primaryColor || '#190192',
          accentColor: book?.styling?.accentColor || '#ea3c4f',
          backgroundColor: book?.styling?.backgroundColor || book?.backgroundColor || '#f8fafc',
          headerColor: book?.styling?.headerColor || book?.headerColor || '#ffffff',
          headerOpacity: book?.styling?.headerOpacity ?? book?.headerOpacity ?? 100,
          textColor: book?.styling?.textColor || '#3b3c26',
          fontFamily: book?.styling?.fontFamily || 'Helvetica, sans-serif',
          logoInvert: book?.styling?.logoInvert ?? 0,
          gallerySidebarBg: book?.styling?.gallerySidebarBg || '#ffffff',
          gallerySidebarTextColor: book?.styling?.gallerySidebarTextColor || '#1f2937',
          gallerySidebarShadow: book?.styling?.gallerySidebarShadow || '#000000'
        }}
      />
      <ViewToggle
          viewMode={viewManager.viewMode}
          onToggle={viewManager.toggleView}
          user={viewManager.user}
          hasManagementAccess={viewManager.hasManagementAccess}
          viewConfig={viewManager.viewConfig}
      /> 
      </>     
    );
  }

  if (book?.templateType === 'profile') {
      return (
        <>
        <ProfileTemplate
          user={userProfile}
          bookId={bookId}
          book={book}
          sections={sectionsData}
          messages={messages}
          bookData={book}
          viewManager={viewManager}
          styling={{
            primaryColor: book?.styling?.primaryColor ?? book?.primaryColor ?? '#6366f1',
            backgroundColor: book?.styling?.backgroundColor ?? book?.backgroundColor ?? '#f8fafc',
            headerColor: book?.styling?.headerColor ?? book?.headerColor ?? '#ffffff',
            headerTextColor: book?.styling?.headerTextColor ?? book?.headerTextColor ?? '#1f2937',
            headerOpacity: book?.styling?.headerOpacity ?? book?.headerOpacity ?? 100,
            accentColor: book?.styling?.accentColor ?? book?.accentColor ?? '#8b5cf6',
            fontFamily: book?.styling?.fontFamily ?? book?.fontFamily ?? 'Helvetica, sans-serif',
            fontSize: book?.profileSettings?.fontSize ?? 'medium',
            textColor: book?.styling?.textColor ?? '#3b3c26',
            borderRadius: book?.profileSettings?.borderRadius ?? 'medium',
            shadowLevel: book?.profileSettings?.shadowLevel ?? 'subtle'
          }}
          profileSettings={book?.profileSettings}
          customCSS={book?.customCSS}
        />
        <ViewToggle
          viewMode={viewManager.viewMode}
          onToggle={viewManager.toggleView}
          user={viewManager.user}
          hasManagementAccess={viewManager.hasManagementAccess}
          viewConfig={viewManager.viewConfig}
        />
        </>
      );
    }

    if (book?.templateType === 'events') {
      return (
          <EventTemplate
            bookId={bookId}
            bookData={book}
            user={userProfile}
            customStyling={{
              primaryColor: book?.styling?.primaryColor || book?.primaryColor || '#10b981',
              backgroundColor: book?.styling?.backgroundColor || book?.backgroundColor || '#f8fafc',
              headerColor: book?.styling?.headerColor || book?.headerColor || '#ffffff',
              headerOpacity: book?.styling?.headerOpacity ?? book?.headerOpacity ?? 100,
              textColor: book?.styling?.textColor || '#3b3c26',
              fontFamily: book?.styling?.fontFamily || 'Helvetica, sans-serif'
            }}
          />
      );
    }

    if (book?.templateType === 'flipbook') {
      return (
        <FlipbookTemplate
          bookId={bookId}
          bookData={book}
          user={userProfile}
          viewManager={viewManager}
          customStyling={{
            primaryColor: book?.styling?.primaryColor || '#8b5cf6',
            backgroundColor: book?.styling?.backgroundColor || '#f8fafc',
            headerColor: book?.styling?.headerColor || '#ffffff',
            fontFamily: book?.styling?.fontFamily || 'Inter, sans-serif'
          }}
        />
      );
    }

    if (book?.templateType === 'leitner') {
      return (
        <>
        <LeitnerTemplate
          bookId={bookId}
          bookData={book}
          user={userProfile}
          viewManager={viewManager}
          styling={{
            primaryColor: book?.styling?.primaryColor ?? book?.primaryColor ?? '#6366f1',
            backgroundColor: book?.styling?.backgroundColor ?? book?.backgroundColor ?? '#f8fafc',
            headerColor: book?.styling?.headerColor ?? book?.headerColor ?? '#ffffff',
            headerTextColor: book?.styling?.headerTextColor ?? book?.headerTextColor ?? '#1f2937',
            headerOpacity: book?.styling?.headerOpacity ?? book?.headerOpacity ?? 100,
            accentColor: book?.styling?.accentColor ?? book?.accentColor ?? '#8b5cf6',
            fontFamily: book?.styling?.fontFamily ?? book?.fontFamily ?? 'Helvetica, sans-serif',
            fontSize: book?.profileSettings?.fontSize ?? 'medium',
            textColor: book?.styling?.textColor ?? '#3b3c26',
            borderRadius: book?.profileSettings?.borderRadius ?? 'medium',
            shadowLevel: book?.profileSettings?.shadowLevel ?? 'subtle'
          }}
        />

        <ViewToggle
            viewMode={viewManager.viewMode}
            onToggle={viewManager.toggleView}
            user={viewManager.user}
            hasManagementAccess={viewManager.hasManagementAccess}
            viewConfig={viewManager.viewConfig}
          />
        </>
      );
    }

    if (book?.templateType === 'websearch') {
      return (
        <>
        <WebSearchTemplate
          bookId={bookId}
          bookData={book}
          user={userProfile}
          logout={logout}
          viewManager={viewManager}
          customStyling={{
            primaryColor: book?.styling?.primaryColor || '#4f46e5',
            backgroundColor: book?.styling?.backgroundColor || '#0f172a',
            headerColor: book?.styling?.headerColor || '#1e293b',
            accentColor: book?.styling?.accentColor || '#8b5cf6',
            fontFamily: book?.styling?.fontFamily || 'Inter, sans-serif'
          }}
        />

        <ViewToggle
            viewMode={viewManager.viewMode}
            onToggle={viewManager.toggleView}
            user={viewManager.user}
            hasManagementAccess={viewManager.hasManagementAccess}
            viewConfig={viewManager.viewConfig}
          />

        </>
      );
    }

    if (book?.templateType === 'requests') {
      return (
        <RequestsTemplate
          bookId={bookId}
          bookData={book}
          user={userProfile}
          logout={logout}
          viewManager={viewManager}
          customStyling={{
            primaryColor: book?.styling?.primaryColor || '#4f46e5',
            backgroundColor: book?.styling?.backgroundColor || '#0f172a',
            headerColor: book?.styling?.headerColor || '#1e293b',
            accentColor: book?.styling?.accentColor || '#8b5cf6',
            fontFamily: book?.styling?.fontFamily || 'Inter, sans-serif'
          }}
        />
      );
    }

    if (book?.templateType === 'course') {
      return (
        <>
          <CourseTemplate
            bookId={bookId}
            bookData={book}
            user={userProfile}
            viewManager={viewManager}
            customStyling={{
              primaryColor: book?.courseSettings?.primaryColor || book?.styling?.primaryColor || '#3b82f6',
              accentColor: book?.courseSettings?.accentColor || book?.styling?.accentColor || '#8b5cf6',
              backgroundColor: book?.courseSettings?.backgroundColor || book?.styling?.backgroundColor || '#ffffff',
              fontFamily: book?.styling?.fontFamily || 'Inter, sans-serif'
            }}
          />
          <ViewToggle
            viewMode={viewManager.viewMode}
            onToggle={viewManager.toggleView}
            user={viewManager.user}
            hasManagementAccess={viewManager.hasManagementAccess}
            viewConfig={viewManager.viewConfig}
          />
        </>
      );
    }

    if (book?.templateType === 'ecommerce') {
      return (
        <>
        <EcommerceTemplate
          bookId={bookId}
          bookData={book}
          user={userProfile}
          viewManager={viewManager}
          customStyling={{
            primaryColor: book?.design?.primaryColor || book?.primaryColor || '#059669',
            backgroundColor: book?.design?.backgroundColor || book?.backgroundColor || '#f8fafc',
            headerColor: book?.design?.headerColor || book?.headerColor || '#ffffff',
            headerOpacity: book?.styling?.headerOpacity ?? book?.headerOpacity ?? 100,
            textColor: book?.styling?.textColor || '#3b3c26',
            fontFamily: book?.design?.fontFamily || 'Helvetica, sans-serif'
          }}
          storeSettings={book?.storeSettings}
        />
        <ViewToggle
            viewMode={viewManager.viewMode}
            onToggle={viewManager.toggleView}
            user={viewManager.user}
            hasManagementAccess={viewManager.hasManagementAccess}
            viewConfig={viewManager.viewConfig}
          />
        </>
      );
    }

    if (book?.templateType === 'booking') {
      return (
        <BookingTemplate
          user={userProfile}
          bookId={bookId}
          bookData={book}
          customStyling={stableCustomStyling}  // ← Stable reference
        />
      );
    }

    if (book?.templateType === 'institution') {
      return (
        <>
          <InstitutionTemplate
            bookId={bookId}
            book={book}
            bookData={book}
            user={userProfile}
            viewManager={viewManager}
            styling={{
              primaryColor: book?.styling?.primaryColor || book?.primaryColor || '#1e40af',
              backgroundColor: book?.styling?.backgroundColor || book?.backgroundColor || '#ffffff',
              headerColor: book?.styling?.headerColor || book?.headerColor || '#1e40af',
              headerTextColor: book?.styling?.headerTextColor || book?.headerTextColor || '#ffffff',
              textColor: book?.styling?.textColor || '#3b3c26',
              accentColor: book?.styling?.accentColor || book?.accentColor || '#3b82f6',
              fontFamily: book?.styling?.fontFamily || book?.fontFamily || 'Inter, system-ui, sans-serif'
            }}
            customStyling={{}}
            customCSS={book?.customCSS}
          />
          <ViewToggle
            viewMode={viewManager.viewMode}
            onToggle={viewManager.toggleView}
            user={viewManager.user}
            hasManagementAccess={viewManager.hasManagementAccess}
            viewConfig={viewManager.viewConfig}
          />
          
        </>
      );
    }

    if (book?.templateType === 'diary') {
      return (
        <>
          <DiaryTemplate
            bookId={bookId}
            bookData={book}
            user={userProfile}
            logout={logout}
            viewManager={viewManager}
            customStyling={{
              primaryColor: book?.styling?.primaryColor || '#6366f1',
              backgroundColor: book?.styling?.backgroundColor || '#f8fafc',
              headerColor: book?.styling?.headerColor || '#ffffff',
              accentColor: book?.styling?.accentColor || '#10b981',
              fontFamily: book?.styling?.fontFamily || 'Inter, sans-serif',
              fontSize: book?.profileSettings?.fontSize ?? 'medium',
              textColor: book?.styling?.textColor ?? '#3b3c26',
              borderRadius: book?.profileSettings?.borderRadius ?? 'medium',
              shadowLevel: book?.profileSettings?.shadowLevel ?? 'subtle'
            }}
          />
          <ViewToggle
            viewMode={viewManager.viewMode}
            onToggle={viewManager.toggleView}
            user={viewManager.user}
            hasManagementAccess={viewManager.hasManagementAccess}
            viewConfig={viewManager.viewConfig}
          />
        </>
      );
    }

    if (book?.templateType === 'organisation') {
      return (
        <OrganisationTemplate 
          bookData={book}
          bookId={bookId}
          user={userProfile}
         
        />
      );
    }
  
    if (!book || !currentSection) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-4">Book not found</h2>
          <button 
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  const getContainerClasses = () => {
    const baseClasses = "min-h-screen";
    
    if (isCustomDomain) {
      return `${baseClasses} custom-domain-book`;
    }
    
    return `${baseClasses} bg-gray-50`;
  };

  return (
    <div 
      className="h-screen flex flex-col book-container helvetica-mode" 
      style={bookStyles}
    >

      {/* Timer Display */}
      <TimerDisplay />

      {/* Resume Modal */}
      {showResumeModal && <ResumeModal />}

      {/* BOOK HEADER */}
      <header 
        className="flex-shrink-0 z-50 border-b"
        style={{ 
          backgroundColor: book?.headerColor || '#ffffff',
          borderColor: book?.borderColor || '#e5e7eb',
          color: book?.headerTextColor || '#000000'
        }}
      >
        <div className="px-0 py-3">
          <div className="flex items-center justify-between">
            {/* Left */}
            <div className="flex items-center gap-3">
              <button
                onClick={toggleToc}
                className="p-2 hover:bg-black hover:bg-opacity-10 rounded-lg transition-colors"
                aria-label="Toggle table of contents"
              >
                <Menu className="h-5 w-5" />
              </button>
              
              {book?.ownerId && (
                <button
                  onClick={() => navigate('/')}
                  className="p-2 hover:bg-black hover:bg-opacity-10 rounded-lg transition-colors"
                  title="Home"
                >
                  <Home className="h-5 w-5" />
                </button>
              )}

              <div className="flex items-center gap-3">
                {book?.logoUrl && (
                  <img 
                    src={book.logoUrl} 
                    alt={`${book.botName} logo`}
                    className="h-8 w-auto object-contain"
                  />
                )}
                <div className="hidden sm:block">
                  <h1 className="font-semibold text-lg">
                    {book.botName}
                  </h1>
                </div>
              </div>
            </div>
            
            {/* Center - Progress */}
            <div className="flex items-center gap-3 flex-1 max-w-md mx-4">
              <div className="flex-1 bg-black bg-opacity-20 rounded-full h-2">
                <div 
                  className="h-full rounded-full transition-all duration-300"
                  style={{ 
                    width: `${progress}%`,
                    backgroundColor: book?.progressColor || '#ffffff'
                  }}
                />
              </div>
              <span className="text-xs whitespace-nowrap font-medium">
                {currentSectionIndex + 1}/{sectionsData.length}
              </span>
            </div>

            {/* Right */}
            <div className="flex items-center gap-1">
              <button className="p-2 hover:bg-black hover:bg-opacity-10 rounded-lg transition-colors">
                <Bookmark className="h-5 w-5" />
              </button>
              <button 
                onClick={handleShare}
                className="p-2 hover:bg-black hover:bg-opacity-10 rounded-lg transition-colors"
              >
                <Share2 className="h-5 w-5" />
              </button>
              {user?.uid === book?.ownerId && (
                <button 
                  onClick={() => setSettingsOpen(true)}
                  className="p-2 hover:bg-black hover:bg-opacity-10 rounded-lg transition-colors"
                  aria-label="Book settings"
                >
                  <Settings className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* MAIN LAYOUT */}
      <div className="flex flex-1 overflow-hidden mt-6 relative">
        
        {/* TABLE OF CONTENTS SIDEBAR - UPDATED: Fixed toggle behavior */}
        <aside className={`
            ${tocOpen ? 'translate-x-0' : '-translate-x-full'}
            fixed inset-y-0 left-0 z-40 bg-white border-r border-gray-200
            transform transition-all duration-300 ease-in-out
            ${tocOpen ? 'lg:static lg:translate-x-0 lg:w-80' : 'lg:w-0 lg:static lg:translate-x-0 lg:overflow-hidden lg:border-r-0'}
            flex flex-col shadow-xl lg:shadow-none
          `}>
          {/* TOC Header */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Sections
              </h2>
              <button
                onClick={closeToc}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* TOC Content */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-2">
              {sectionsData.map((section, index) => (
                <button
                  key={index}
                  onClick={() => jumpToSection(index)}
                  className={`
                    w-full text-left p-3 rounded-lg transition-all group hover:bg-gray-50
                    ${index === currentSectionIndex 
                      ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                      : 'text-gray-700 hover:text-gray-900'
                    }
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium truncate">
                        {section.name}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">
                        {section.messages.length} message{section.messages.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <div className="ml-2">
                      {section.messages.some(msg => msg.mediaUrl) && (
                        <PlayCircle className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Search */}
          <div className="p-4 border-t border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search content..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {tocOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
            onClick={closeToc}
          />
        )}

        {/* MAIN CONTENT */}
        <main className="flex-1 overflow-y-auto">
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-4xl mx-auto p-2 sm:p-0">
              <article className="bg-white rounded-xl shadow-sm border border-gray-100">
                {/* Section Header */}
                <header className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-4 border-b border-gray-100 rounded-t-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900 mb-1">
                        {currentSection.name}
                      </h1>
                      <div className="flex items-center gap-3 text-sm text-gray-600">
                        <span>Section {currentSectionIndex + 1} of {sectionsData.length}</span>
                        <span>•</span>
                        <span>{currentSection.messages.length} message{currentSection.messages.length !== 1 ? 's' : ''}</span>
                        {currentSection.messages.some(msg => msg.type === 'math') && (
                          <>
                            <span>•</span>
                            <span className="flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded">
                              <Calculator className="h-3 w-3" />
                              Math Content
                            </span>
                          </>
                        )}
                        {currentSection.messages.some(msg => msg.mediaUrl) && (
                          <>
                            <span>•</span>
                            <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded">
                              <PlayCircle className="h-3 w-3" />
                              Media Content
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </header>

                {/* Section Content - All messages in current section */}
                <div className="p-4">
                  {currentSection.messages.map((message, index) => 
                    renderMessage(message, index)
                  )}
                </div>

                {/* SECTION NAVIGATION - Only at end of section */}
                <div className="border-t border-gray-100 bg-gray-50 rounded-b-xl p-4">
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => handleNavigation('prev')}
                      disabled={currentSectionIndex === 0}
                      className={`
                        flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all
                        ${currentSectionIndex === 0
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 hover:bg-gray-100 active:scale-95 border border-gray-200'
                        }
                      `}
                    >
                      <ChevronLeft className="h-5 w-5" />
                      Previous 
                    </button>
                    
                    <button
                      onClick={() => handleNavigation('next')}
                      disabled={currentSectionIndex === sectionsData.length - 1}
                      className={`
                        flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all
                        ${currentSectionIndex === sectionsData.length - 1
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'text-white hover:opacity-90 active:scale-95 border-0'
                        }
                      `}
                      style={{
                        backgroundColor: currentSectionIndex === sectionsData.length - 1 
                          ? undefined 
                          : book?.primaryColor || '#2563eb'
                      }}
                    >
                      Next
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Final spacer */}
                <div className="h-2" />
              </article>
            </div>
          </div>
        </main>
      </div>

      {/* BOOK SETTINGS MODAL */}
      {settingsOpen && (
        <BookSettingsModal
          book={book}
          isOpen={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          onSave={handleBookUpdate}
        />
      )}

      {/* ADDED: SHARE MODAL */}
      {shareOpen && (
        <ShareModal
          book={book}
          isOpen={shareOpen}
          onClose={() => setShareOpen(false)}
        />
      )}

      {/* Custom Styles */}
      <style jsx>{`
        .book-container {
          background-color: var(--book-background);
        }
        
        .message-item {
          scroll-margin-top: 40px;
        }
        
        .interactive-element {
          animation: fadeIn 0.3s ease-in-out;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .media-content {
          animation: slideIn 0.4s ease-out;
        }
        
        @keyframes slideIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        
        @media (max-width: 768px) {
          .message-item {
            margin-bottom: 1.5rem;
          }
        }
      `}</style>

      <ControlBar />

    </div>
  );
};

export default BookViewer;